import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChallengeId } from '../hooks/challenge/useChallengeId'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { useCreateTeam } from '../hooks/team/useCreateTeam'
import { useTeams } from '../hooks/team/useTeams'
import { TEAM_NAME_MAX_LENGTH, validateTeamName } from '../logic/teamNameUtils'
import {
  challengePaymentIntroPath,
  challengeTeamPath,
  ScreenPath,
} from '../navigation'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import TextField from '../ui/primitives/TextField'
import styles from '../ui/team/TeamEntry.module.css'

export default function CreateTeamScreen() {
  const navigate = useNavigate()
  const challengeId = useChallengeId()
  const { teamID, isEntitled } = useChallengeAccess(challengeId)
  const { data: teams } = useTeams(challengeId)
  const { mutateAsync: createTeamMutation } = useCreateTeam(challengeId)

  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const teamName = name.trim()

    if (teamID) {
      navigate(`${ScreenPath.team}/${teamID}`)
      return
    }

    const error = validateTeamName(
      teamName,
      (teams ?? []).map((team) => team.name),
    )
    if (error) {
      setNameError(error)
      return
    }

    if (!isEntitled) {
      navigate(
        challengePaymentIntroPath(challengeId) +
          '?sourcePath=' +
          encodeURIComponent(window.location.pathname) +
          '&action=' +
          encodeURIComponent(
            JSON.stringify({
              type: 'CREATE_TEAM',
              payload: { name: teamName },
            }),
          ),
      )
    } else {
      setLoading(true)
      const result = await createTeamMutation({ name: teamName })
      setLoading(false)
      navigate(`${ScreenPath.team}/${result.team.id}`, { replace: true })
    }
  }

  return (
    <SimplePageLayout title="Create a team" backTo={challengeTeamPath(challengeId)}>
      <div className={styles.stack}>
        <div className={styles.panel}>
          <form className={styles.form} onSubmit={onSubmit}>
            <TextField
              label="Team name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameError('')
              }}
              error={nameError}
              autoFocus
              maxLength={TEAM_NAME_MAX_LENGTH}
            />
            <p className={styles.hint}>Please keep your team name PG-13, kids can do Relentless too!</p>
            <div className={styles.actions}>
              <Button type="submit" disabled={loading || !name.trim()}>
                {loading ? 'Creating…' : 'Create team'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </SimplePageLayout>
  )
}
